import { z } from 'zod';

export const LocationCoordinateSchema = z.object({
  lat: z.number(),
  lng: z.number(),
});

export const LocationBoundsSchema = z
  .object({
    northeast: LocationCoordinateSchema.nullable().optional(),
    southwest: LocationCoordinateSchema.nullable().optional(),
  })
  .partial();

export const LocationSuggestionSchema = z.object({
  id: z.string(),
  label: z.string(),
  secondaryLabel: z.string().nullable().optional(),
  center: LocationCoordinateSchema,
  bounds: LocationBoundsSchema.nullable().optional(),
  approxRadiusM: z.number().nullable().optional(),
  countryCode: z.string().nullable().optional(),
  confidence: z.number().nullable().optional(),
  source: z.enum(['cache', 'mapbox']),
});

export const LocationSearchResponseSchema = z.object({
  query: z.string(),
  results: z.array(LocationSuggestionSchema),
});

export const ExploreMapSearchQuerySchema = z.object({
  q: z.string().trim().min(2).max(120),
  limit: z.coerce.number().int().min(1).max(10).default(7),
});

export const NetworkCoverageBucketSchema = z.object({
  total: z.number().int().min(0),
  resolved: z.number().int().min(0),
  withLocation: z.number().int().min(0),
  meaningful: z.number().int().min(0),
  geocoded: z.number().int().min(0),
  pendingGeocode: z.number().int().min(0),
});

export const NetworkCoverageSummarySchema = z.object({
  followers: NetworkCoverageBucketSchema,
  following: NetworkCoverageBucketSchema,
  mutual: NetworkCoverageBucketSchema,
  all: NetworkCoverageBucketSchema,
  metadata: z.object({
    generatedAt: z.date(),
  }),
});

export const ExploreMapPointsQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(10_000).default(5000),
  type: z.enum(['followers', 'following', 'mutual', 'all']).default('all'),
  bbox: z.string().optional(),
  center: z.string().optional(),
  radiusM: z.coerce.number().min(0).optional(),
  zoom: z.coerce.number().optional(),
});

export const ExploreMapFeatureSchema = z.object({
  type: z.literal('Feature'),
  geometry: z.object({
    type: z.literal('Point'),
    coordinates: z.tuple([z.number(), z.number()]),
  }),
  properties: z.object({
    profileId: z.string(),
    username: z.string().nullable(),
    name: z.string().nullable(),
    avatarUrl: z.string().nullable(),
    approxRadiusM: z.number().nullable().optional(),
    followersCount: z.number().nullable().optional(),
    followingCount: z.number().nullable().optional(),
    tweetCount: z.number().nullable().optional(),
    verified: z.boolean().nullable().optional(),
    location: z.string().nullable().optional(),
    resolvedLocation: z.string().nullable().optional(),
  }),
});

export const ExploreMapFeatureCollectionSchema = z.object({
  type: z.literal('FeatureCollection'),
  features: z.array(ExploreMapFeatureSchema),
  meta: z.object({
    totals: z.object({
      totalProfiles: z.number().int(),
      meaningfulProfiles: z.number().int(),
      locatedProfiles: z.number().int(),
      queuedKeys: z.number().int(),
      unknownProfiles: z.number().int(),
    }),
    realtime: z
      .object({
        scope: z.string().trim().min(1),
      })
      .optional(),
  }),
});

export type ExploreNetworkStatsRoute = never;
export type ExploreMapPointsRoute = never;
export type ExploreMapSearchRoute = never;
